<template>
    <BaseDetails>
        <template v-if="isRevoked">
            <div class="revocation-notice">
                <div class="revocation-notice__icon">
                    <i class="mdi mdi-information-outline text-primary"></i>
                </div>
                <span class="revocation-notice__text text-quote" v-html="$t('orderRevocation.revokedModalText')"></span>
            </div>

            <div class="separator"></div>
        </template>

        <h4 v-if="hasCombinedOrders" v-html="$t('shopEuropamaut.productName')"></h4>
        <div class="product split">
            <ResourcePicture v-if="!hasCombinedOrders" alt="" resource-name="/assets/img/vignette.png" />
            <dl class="product__details">
                <template v-if="!hasCombinedOrders">
                    <dd class="product__name font-weight-bold" v-if="order.product_type === 'route_toll'">
                        {{ order.product.name }} - {{ order.option.name }}
                    </dd>
                    <dd class="product__name font-weight-bold" v-else>
                        {{ order.product.name }} - {{ $t(`shop.vehicle_${order.option.vehicle_type}`) }}
                    </dd>
                    <dt class="text-quote">{{ $t('shop.estimated_validity_start') }}:</dt>
                    <dd class="font-weight-bold">
                        {{ order.estimated_validity_start_string }}
                        <template v-if="order.order_timezone"> ({{ $t('shop.shop_timezone_description') }}) </template>
                    </dd>
                    <template v-if="order.order_timezone">
                        <dd>
                            {{ order.estimated_validity_start_timezone_string }} ({{
                                $t('shop.customer_timezone_description')
                            }})
                        </dd>
                    </template>
                </template>
                <template v-else>
                    <dd v-for="combinedSuborder in combinedSuborders" :key="combinedSuborder.id" class="mb-2">
                        <div class="product-vehicle-name">
                            <strong>{{ getProductName(combinedSuborder) }}</strong>
                            {{ getVehicleNameFromCombinedSuborder(combinedSuborder) }}
                        </div>
                        <div>
                            <small>
                                <span class="text-quote">{{ $t('shop.estimated_validity_start') }}:</span>
                                {{ combinedSuborder.estimated_validity_start_string }}
                            </small>
                        </div>
                    </dd>
                </template>
            </dl>
        </div>

        <div class="separator"></div>

        <dl class="price split">
            <dt class="price__text text-quote">
                <span class="heading tight d-block" v-html="$t('orderCheckout.total')"></span>
                <span class="subtext d-block" v-html="$t('orderCheckout.total_subtext')"></span>
            </dt>
            <dd class="price__total text-quote">
                <price
                    :price="order.unpaid_amount"
                    :currency-in-newline="true"
                    container-class="heading"
                    price-class="price__total__main"
                    converted-price-class="price__total__sub"
                ></price>
            </dd>
        </dl>

        <div class="payments" v-if="giftVoucherPayments.length > 0">
            <ol>
                <li class="text-muted" v-for="payment in giftVoucherPayments" :key="payment.id">
                    {{ $t('giftVoucher.infoAmountCredited', {amount: payment.amount.toFixed(2)}) }}
                </li>
            </ol>
        </div>

        <div class="separator"></div>

        <dl class="details split">
            <dt class="details__text text-quote">
                <span class="heading tight" v-html="$t('orderCheckout.license')"></span>
                <br>
                <span class="subtext" v-html="$t('orderCheckout.license_subtext')"></span>
            </dt>
            <dd class="details__licenseplate text-quote">
                <span class="heading">{{ order.license }}</span>
            </dd>
        </dl>

        <template v-if="!isRevoked">
            <div class="separator mb-3 text-quote" v-html="$t('orderCheckout.advantages-title')"></div>

            <div class="advantages">
                <div v-for="advantage in advantages" :key="advantage.id" class="col d-flex">
                    <div class="advantages-entry text-quote">
                        <i :class="'mdi-' + advantage.icon" class="mdi text-primary advantage-icon h3"></i>
                        <h3 class="advantage-header">{{ advantage.title }}</h3>
                        <p class="advantage-content">{{ advantage.description }}</p>
                    </div>
                </div>
            </div>
        </template>
    </BaseDetails>
</template>

<script>
import BaseDetails from './Base.vue';
import ResourcePicture from '../../../ResourcePicture.vue';

export default {
    name: 'Order',
    components: { ResourcePicture, BaseDetails },
    props: ['order', 'combinedSuborders', 'advantages', 'formatter', 'giftVoucherPayments', 'isRevoked'],
    inject: ['settings'],
    methods: {
        getProductName(order) {
            const obj = JSON.parse(order.product_names);
            return obj[this.$language] ?? obj['en'];
        },
        getVehicleNameFromCombinedSuborder(suborder) {
            if (!suborder) {
                return '';
            }

            const option = suborder.additionalDetails.product.options.find((opt) => {
                return opt.id === suborder.option_id;
            });

            if (!option) {
                return '';
            }

            let lang = '';

            if (option.vehicleNames === null || !option.vehicleNames || option.vehicleNames.length <= 0) {
                lang = option.labels[this.$language] ?? option.labels['en'];
            } else {
                lang = option.vehicleNames[this.$language] ?? option.vehicleNames['en'];
            }

            return '- ' + lang.replace(/<[^>]*>/g, '');
        },
    },
    computed: {
        hasCombinedOrders() {
            return this.combinedSuborders.length > 0;
        },
    },
};
</script>

<style lang="scss">
.product {
    picture {
        width: 25%;

        & > img {
            width: 100%;
        }
    }
}
</style>

<style lang="scss" scoped>
.revocation-notice {
    display: flex;
    align-items: center;
    gap: 1rem;

    i {
        font-size: xxx-large;
    }
}

.product {
    gap: 1.5rem;

    & > .product__details {
        .product__name {
            font-size: large;
        }
    }
}

.advantages {
    display: grid;
    grid-template-columns: 1fr 1fr;

    @media (max-width: 767px) {
        grid-template-columns: 1fr;

        & > div {
            justify-content: start;
            padding: 0;
        }
    }

    h3 {
        font-size: medium;
    }
}

.price, .details {
    justify-content: space-between;
}

.price__total {
    white-space: nowrap;

    ::v-deep &__main {
        line-height: 1;
    }

    ::v-deep &__sub {
        margin-top: -5px;
    }
}

.payments {
    ol, li {
        list-style: none;
        padding: 0;
    }
}

dl, dd, dt {
    margin: 0;
    font-weight: inherit;
}
</style>
